import React, { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Loader2, RotateCcw, PlayCircle, Anchor, Volume2 } from "lucide-react";

const GREETING = "Ciao, sono l'assistente di Yacht Food Cost. Ti rubo solo 5 minuti del tuo tempo. Lascia che ti mostri come tenere sotto controllo cambusa e food cost, risolvendo un problema che vivi ogni giorno a bordo. Meno tempo sui conti, più tempo ai fornelli. Scopriamolo insieme?";

const FAQS = [
  { id: "prezzo", q: "💶 Quanto costa?", a: "€1.490 a stagione, tutto incluso. E puoi provarla 30 giorni gratis, senza impegno." },
  { id: "come", q: "⚙️ Come funziona?", a: "Fotografi le fatture e l'app calcola da sola il food cost per persona al giorno, ti avvisa su budget e scadenze. In un minuto hai tutto sotto controllo.", video: true },
  { id: "cosa", q: "🧾 Cosa fa?", a: "Cambusa, acquisti e fatture, food cost, budget, HACCP e scadenze, menù con allergeni, ospiti, ordini ai fornitori e report per l'armatore. Tutto in un unico posto." },
  { id: "chi", q: "⛵ Per chi è?", a: "Per chef di bordo, comandanti e armatori di yacht da diporto e charter. L'armatore vede tutto, in sola lettura." },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

function Avatar({ size = "h-10 w-10", speaking = false }) {
  return <img src="/assistant-avatar.png" alt="Assistente" className={`${size} rounded-full border-2 border-gold object-cover ${speaking ? "animate-pulse ring-4 ring-gold/40" : ""}`} />;
}

export default function Assistente() {
  const [messages, setMessages] = useState([{ from: "bot", text: GREETING }]);
  const [typing, setTyping] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const playedRef = useRef(false);

  const playWelcome = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().then(() => { playedRef.current = true; }).catch(() => {});
  };

  useEffect(() => {
    audioRef.current = new Audio("/assistant-welcome.mp3");
    const a = audioRef.current;
    const onPlay = () => setSpeaking(true);
    const onEnd = () => setSpeaking(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onEnd);
    // try autoplay; browsers may block until first interaction
    a.play().then(() => { playedRef.current = true; }).catch(() => {
      const kick = () => { if (!playedRef.current) playWelcome(); window.removeEventListener("pointerdown", kick); };
      window.addEventListener("pointerdown", kick, { once: true });
    });
    return () => {
      a.pause();
      a.removeEventListener("play", onPlay);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onEnd);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing, showForm]);

  const botSay = (text) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { from: "bot", text }]);
    }, 600);
  };

  const askFaq = (faq) => {
    setMessages((m) => [...m, { from: "user", text: faq.q }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { from: "bot", text: faq.a, video: faq.video }]);
    }, 600);
  };

  const reset = () => {
    setMessages([{ from: "bot", text: GREETING }]);
    setShowForm(false);
    setForm({ name: "", email: "" });
    setDone(false);
    playWelcome();
  };

  const openForm = () => {
    setShowForm(true);
    botSay("Perfetto! Lasciami nome ed email: ti ricontattiamo con la prova gratuita. 👇");
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name) { toast.error("Scrivi il tuo nome"); return; }
    if (!EMAIL_RE.test(email)) { toast.error("Controlla l'indirizzo email"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", { name, email });
      setShowForm(false);
      setDone(true);
      setMessages((m) => [...m, { from: "user", text: `${name} · ${email}` }, { from: "bot", text: `Grazie ${name}! Ti ricontattiamo prestissimo. Buon vento ⚓` }]);
      setTimeout(reset, 7000);
    } catch {
      toast.error("Qualcosa è andato storto, riprova");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="assistente-page" className="flex min-h-screen flex-col items-center bg-navy px-4 py-6 text-white">
      {/* Header */}
      <div className="flex w-full max-w-2xl items-center justify-between">
        <div className="flex items-center gap-2">
          <Anchor className="h-5 w-5 text-gold" />
          <span className="font-head text-lg font-bold">YACHT FOOD COST</span>
        </div>
        <button data-testid="assistente-reset" onClick={reset} className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm text-slate-200 hover:bg-white/20">
          <RotateCcw className="h-4 w-4" /> Ricomincia
        </button>
      </div>

      {/* Hero avatar */}
      <div className="mt-6 flex flex-col items-center text-center">
        <Avatar size="h-24 w-24" speaking={speaking} />
        <h1 className="mt-3 font-head text-2xl font-bold text-gold">Assistente di bordo</h1>
        <p className="mt-1 max-w-md text-sm text-slate-300">Meno tempo sui conti, più tempo ai fornelli.</p>
        <button data-testid="assistente-listen" onClick={playWelcome} className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-gold hover:bg-white/20">
          <Volume2 className="h-4 w-4" /> Ascolta il benvenuto
        </button>
      </div>

      {/* Chat card */}
      <div className="mt-6 flex w-full max-w-2xl flex-1 flex-col rounded-3xl bg-[#F4F1EA] shadow-2xl">
        <div ref={scrollRef} data-testid="assistente-chat" className="flex-1 space-y-3 overflow-y-auto p-5" style={{ maxHeight: "42vh", minHeight: "220px" }}>
          {messages.map((m, i) => (
            m.from === "bot" ? (
              <div key={i} className="flex items-start gap-2">
                <Avatar size="h-9 w-9" />
                <div className="max-w-[80%]">
                  <div data-testid="assistente-bot-msg" className="rounded-2xl rounded-tl-sm bg-navy px-4 py-3 text-[15px] leading-relaxed text-white">{m.text}</div>
                  {m.video && (
                    <a data-testid="assistente-video-link" href="/kit/tutorial-it.mp4" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-bold text-black hover:brightness-105">
                      <PlayCircle className="h-4 w-4" /> Guarda il video (1 min)
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-gold px-4 py-3 text-[15px] font-medium text-black">{m.text}</div>
              </div>
            )
          ))}
          {typing && (
            <div className="flex items-center gap-2">
              <Avatar size="h-9 w-9" />
              <div className="flex gap-1 rounded-2xl bg-navy px-4 py-3">
                <span className="h-2 w-2 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        {/* Action zone */}
        <div className="border-t border-slate-200 p-4">
          {done ? (
            <div data-testid="assistente-done" className="rounded-2xl bg-emerald-50 py-4 text-center font-semibold text-emerald-700">
              Contatto ricevuto ✓ — grazie!
            </div>
          ) : showForm ? (
            <form onSubmit={submit} className="space-y-3" data-testid="assistente-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="assistente-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Il tuo nome" className="h-12 rounded-xl border-2 border-slate-200 px-4 text-navy outline-none focus:border-gold" />
                <input data-testid="assistente-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="La tua email" className="h-12 rounded-xl border-2 border-slate-200 px-4 text-navy outline-none focus:border-gold" />
              </div>
              <button data-testid="assistente-submit" type="submit" disabled={submitting} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-navy text-lg font-bold text-white hover:bg-navy-hover disabled:opacity-60">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Invia e ricevi la prova gratuita
              </button>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {FAQS.map((f) => (
                  <button key={f.id} data-testid={`assistente-faq-${f.id}`} onClick={() => askFaq(f)} className="rounded-full border-2 border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-gold hover:bg-gold/10">
                    {f.q}
                  </button>
                ))}
              </div>
              <button data-testid="assistente-cta" onClick={openForm} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gold text-lg font-bold text-black transition-transform hover:-translate-y-0.5">
                Voglio provarla — lasciami i contatti
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-5 text-xs text-slate-400">€1.490 / stagione · 30 giorni gratis · Salone Nautico di Genova</p>
    </div>
  );
}
